import { mergeAttributes, Node } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { ALL_TEMPLATE_FIELD_NAMES } from '../../../doc-engine/templateFields';
import type { TTransformFragment } from '../../extensions/PasteRule';
import getEmptyFormFieldPlugin from '../../plugins/getEmptyFormFieldPlugin';
import getFormFieldArrowPlugin from '../../plugins/getFormFieldArrowPlugin';
import getFormFieldDragGuardPlugin from '../../plugins/getFormFieldDragGuardPlugin';
import { BACKSPACE_KEYS, DELETE_KEYS, FORM_FIELD_CLASS_NAME, FORM_FIELD_NODE_NAME } from './constants';
import { isFieldInCard } from './formFieldHelpers';
import FormFieldView from './FormFieldView';

declare module '@tiptap/core' {
  interface Storage {
    // Insertion and Duplicate ask for the label of a new field; its card or popup focuses it once.
    [FORM_FIELD_NODE_NAME]: { focusLabelOfFieldId: string | undefined };
  }
}

// An input with a text selection, where copy works on the typed text.
const isTextInput = (target: EventTarget | null): boolean =>
  target instanceof HTMLTextAreaElement || (target instanceof HTMLInputElement && target.selectionStart !== null);

// A copy on a card's control changes nothing, so the editor takes it; a cut or paste there stays the control's.
const isClipboardEventForEditor = ({ type, target }: Event): boolean => {
  if (type === 'copy') return !isTextInput(target);
  if (type !== 'cut' && type !== 'paste') return false;
  return !(target instanceof Element && target.closest('button, input, select, textarea'));
};

export type TFormFieldOptions = {
  // The editor's own rule for copied content, so Duplicate gives the copy fresh ids like a paste.
  copyFields: TTransformFragment;
};

/** A card around one existing field, which keeps its own settings: the card moves, copies and deletes as one unit. */
const FormFieldNode = Node.create<TFormFieldOptions>({
  name: FORM_FIELD_NODE_NAME,
  // The section's content names the card by its group, as a content expression cannot name a node with a dash.
  group: 'rootOnly formField',
  // Optional: deleting the field leaves an empty card to clean up, not a refilled unconfigured field.
  // Line first: clearNodes makes a textblock the first type its parent allows, which must not be a block field.
  content: '(formFieldLine | blockTemplateField)?',
  isolating: true,
  defining: true,
  // A gap cursor beside the field would type a second line into the card.
  allowGapCursor: false,

  // `id` comes from the `addUniqueIdAttribute()` wrapper; only the field inside has a `fieldId`.

  addStorage() {
    return { focusLabelOfFieldId: undefined };
  },

  parseHTML() {
    return [{ tag: `div.${FORM_FIELD_CLASS_NAME}` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: FORM_FIELD_CLASS_NAME }), 0];
  },

  addNodeView() {
    const renderCard = ReactNodeViewRenderer(FormFieldView);
    return (props) => {
      const nodeView = renderCard(props);
      const { stopEvent } = nodeView;
      if (stopEvent) {
        nodeView.stopEvent = (event) => !isClipboardEventForEditor(event) && stopEvent.call(nodeView, event);
      }
      return nodeView;
    };
  },

  addKeyboardShortcuts() {
    // A card's field goes only with its card, which its Delete button or its own selection removes.
    const keepSelectedField = () => {
      const { selection, doc } = this.editor.state;
      return (
        selection instanceof NodeSelection &&
        ALL_TEMPLATE_FIELD_NAMES.has(selection.node.type.name) &&
        isFieldInCard(doc, selection.from)
      );
    };
    return Object.fromEntries([...BACKSPACE_KEYS, ...DELETE_KEYS].map((key) => [key, keepSelectedField]));
  },

  addProseMirrorPlugins() {
    return [
      getEmptyFormFieldPlugin(this.type),
      getFormFieldDragGuardPlugin(),
      getFormFieldArrowPlugin(),
      new Plugin({
        key: new PluginKey('formFieldControls'),
        props: {
          handleDOMEvents: {
            // Tiptap passes a press on a control's icon to ProseMirror, whose caret would close the card first.
            mousedown: (_view, event) =>
              !!(event.target as Element).closest(`.${FORM_FIELD_CLASS_NAME} :is(button, label)`),
          },
          // A click on the card's own space selects it, as one on an image does; a click in its answer lands deeper.
          handleClickOn: (view, _pos, node, nodePos, _event, direct) => {
            if (!view.editable || !direct || node.type !== this.type) return false;
            view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos)));
            return true;
          },
        },
      }),
    ];
  },
});

export default FormFieldNode;
